import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { SearchSmIcon, XCloseIcon } from "@vetty/icons";
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Input,
  Select,
  Tabs,
  TabsItem,
} from "@/components";
import {
  axiosInstance,
  handleAxiosError,
  type ApiResponse,
} from "@/helpers/axios";
import type { Group } from "@/app/groups/_types/groups.types";
import { CREATE_ACCOUNT_ROLE_OPTIONS } from "../../_constants/accounts.const";
import type { AccountRole } from "../../_types/accounts.types";
import {
  CREATE_ACCOUNT_TAB_LABELS,
  buildCreateAccountPayload,
  formatGroupSeatLabel,
  validateCreateAccountFields,
  type CreateAccountTab,
  type SecurityMode,
} from "./create-account-drawer.helpers";
import styles from "./CreateAccountDrawer.module.scss";

type CreateAccountDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

type CreatedAccountPayload = {
  id: string;
  email: string;
  role: string;
  groupIds?: string[];
  inviteEmailSent?: boolean;
};

type GroupsMeta = {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

type SelectedGroupChip = {
  id: string;
  label: string;
};

const EMPTY_META: GroupsMeta = {
  total: 0,
  page: 1,
  limit: 10,
  totalPages: 1,
};

export const CreateAccountDrawer = ({ isOpen, onClose }: CreateAccountDrawerProps) => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<CreateAccountTab>("identity");
  const [invalidTab, setInvalidTab] = useState<CreateAccountTab | null>(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AccountRole>("user");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");

  const [selectedGroups, setSelectedGroups] = useState<SelectedGroupChip[]>([]);
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [searchItems, setSearchItems] = useState<Group[]>([]);
  const [searchMeta, setSearchMeta] = useState<GroupsMeta>(EMPTY_META);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const [securityMode, setSecurityMode] = useState<SecurityMode>("invite");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitNotice, setSubmitNotice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** Hard lock: Button is CSS-disabled only; blocks click/Enter POST retries after create. */
  const createLockedRef = useRef(false);

  const selectedIds = useMemo(
    () => selectedGroups.map((g) => g.id),
    [selectedGroups],
  );

  const resetForm = () => {
    createLockedRef.current = false;
    setTab("identity");
    setInvalidTab(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setRole("user");
    setFirstNameError("");
    setLastNameError("");
    setEmailError("");
    setSelectedGroups([]);
    setSearch("");
    setSearchDebounced("");
    setPage(1);
    setSearchItems([]);
    setSearchMeta(EMPTY_META);
    setSearchError(null);
    setSecurityMode("invite");
    setPassword("");
    setPasswordError("");
    setSubmitError(null);
    setSubmitNotice(null);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!isOpen) return;
    resetForm();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handle = window.setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [search, isOpen]);

  useEffect(() => {
    if (!isOpen || tab !== "access") return;

    let cancelled = false;
    const load = async () => {
      setSearchLoading(true);
      setSearchError(null);
      try {
        const response = await axiosInstance.get<
          ApiResponse<{ items: Group[]; meta?: GroupsMeta }>
        >("/v2/groups", {
          params: {
            kind: "all",
            archived: "0",
            q: searchDebounced || undefined,
            page,
            limit: 10,
          },
        });
        if (cancelled) return;
        const payload = response.data.data;
        setSearchItems(payload?.items ?? []);
        setSearchMeta(payload?.meta ?? { ...EMPTY_META, page });
      } catch (error) {
        if (cancelled) return;
        setSearchItems([]);
        setSearchError(
          handleAxiosError(error)?.message ?? "Could not load groups.",
        );
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, tab, searchDebounced, page]);

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const addGroup = (group: Group) => {
    setSelectedGroups((prev) => {
      if (prev.some((g) => g.id === group.id)) return prev;
      return [...prev, { id: group.id, label: group.name }];
    });
  };

  const removeGroup = (id: string) => {
    setSelectedGroups((prev) => prev.filter((g) => g.id !== id));
  };

  const handleSubmit = async () => {
    // Guard must stop POST even when Button only applies a disabled class (Enter still submits).
    if (createLockedRef.current || isSubmitting || Boolean(submitNotice)) {
      return;
    }

    const validation = validateCreateAccountFields({
      firstName,
      lastName,
      email,
      securityMode,
      password,
    });
    setFirstNameError(validation.errors.firstName);
    setLastNameError(validation.errors.lastName);
    setEmailError(validation.errors.email);
    setPasswordError(validation.errors.password);
    if (!validation.ok) {
      setInvalidTab(validation.invalidTab);
      if (validation.invalidTab) setTab(validation.invalidTab);
      return;
    }

    setInvalidTab(null);
    setSubmitError(null);
    // Never clear submitNotice here — that would re-arm create after invite miss.

    const body = buildCreateAccountPayload({
      firstName,
      lastName,
      email,
      role,
      securityMode,
      password,
      groupIds: selectedIds,
    });

    createLockedRef.current = true;
    try {
      setIsSubmitting(true);
      const response = await axiosInstance.post<ApiResponse<CreatedAccountPayload>>(
        "/v2/accounts",
        body,
      );

      await queryClient.invalidateQueries({ queryKey: ["accounts"] });
      await queryClient.invalidateQueries({ queryKey: ["groups"] });

      if (response.data.data?.inviteEmailSent === false) {
        setSubmitNotice(
          "Account created, but the set-password invite email was not sent because transactional email is not configured in this environment. Use Resend demo invitation later — retrying Create will not duplicate this account.",
        );
        return;
      }

      onClose();
    } catch (error) {
      createLockedRef.current = false;
      setSubmitError(
        handleAxiosError(error)?.message ??
          "Could not create account. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const busy = isSubmitting || Boolean(submitNotice) || createLockedRef.current;

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Create account">
      <DrawerHeader title="Create account" onClose={handleClose} closeDisabled={isSubmitting} />
      <form
        className={styles["comp__stack"]}
        onSubmit={(e) => {
          e.preventDefault();
          if (createLockedRef.current || isSubmitting || Boolean(submitNotice)) {
            return;
          }
          void handleSubmit();
        }}
      >
        <DrawerBody className={styles["comp__body"]}>
          <div className={styles["comp"]}>
            <div className={styles["comp__tabs-wrap"]}>
              <Tabs
                value={tab}
                onChange={(v) => setTab(v as CreateAccountTab)}
                variant="secondary"
                size="medium"
              >
                {(Object.keys(CREATE_ACCOUNT_TAB_LABELS) as CreateAccountTab[]).map(
                  (key) => (
                    <TabsItem
                      key={key}
                      value={key}
                      className={
                        invalidTab === key ? styles["comp__tab-error"] : undefined
                      }
                      endContent={invalidTab === key ? "•" : undefined}
                    >
                      {CREATE_ACCOUNT_TAB_LABELS[key]}
                    </TabsItem>
                  ),
                )}
              </Tabs>
            </div>

            {tab === "identity" ? (
              <div className={styles["comp__panel"]} role="tabpanel">
                <div className={styles["comp__intro"]}>
                  <h3 className={styles["comp__section-title"]}>Identity</h3>
                  <p className={styles["comp__subtitle"]}>
                    Name, email, and role. Default role is user. Group flags never grant
                    privileged roles.
                  </p>
                </div>
                <div className={styles["comp__panel-content"]}>
                  <div className={styles["comp__fields"]}>
                    <div className={styles["comp__row"]}>
                      <Input
                        id="create-account-first-name"
                        label="First name"
                        value={firstName}
                        placeholder="Jane"
                        required
                        disabled={busy}
                        error={firstNameError}
                        onChange={(value) => {
                          setFirstName(value);
                          setFirstNameError("");
                          setSubmitError(null);
                        }}
                      />
                      <Input
                        id="create-account-last-name"
                        label="Last name"
                        value={lastName}
                        placeholder="Doe"
                        required
                        disabled={busy}
                        error={lastNameError}
                        onChange={(value) => {
                          setLastName(value);
                          setLastNameError("");
                          setSubmitError(null);
                        }}
                      />
                    </div>
                    <Input
                      id="create-account-email"
                      label="Email"
                      type="text"
                      value={email}
                      placeholder="name@company.com"
                      required
                      disabled={busy}
                      error={emailError}
                      onChange={(value) => {
                        setEmail(value);
                        setEmailError("");
                        setSubmitError(null);
                      }}
                    />
                    <Select
                      id="create-account-role"
                      label="Role"
                      fullWidth
                      size="medium"
                      variant="secondary"
                      value={role}
                      disabled={busy}
                      onChange={(value) => setRole(value as AccountRole)}
                    >
                      {CREATE_ACCOUNT_ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === "access" ? (
              <div
                className={`${styles["comp__panel"]} ${styles["comp__panel--access"]}`}
                role="tabpanel"
              >
                <div className={styles["comp__intro"]}>
                  <h3 className={styles["comp__section-title"]}>Access &amp; groups</h3>
                  <p className={styles["comp__subtitle"]}>
                    Optionally attach existing groups. Memberships reserve seats when a
                    pool license exists. No license or Stripe is created here.
                  </p>
                </div>
                <div className={styles["comp__panel-content"]}>
                  <div className={styles["comp__access-top"]}>
                    {selectedGroups.length > 0 ? (
                      <div className={styles["comp__chips"]}>
                        {selectedGroups.map((group) => (
                          <span key={group.id} className={styles["comp__chip"]}>
                            <span className={styles["comp__chip-label"]}>
                              {group.label}
                            </span>
                            <button
                              type="button"
                              className={styles["comp__chip-remove"]}
                              aria-label={`Remove ${group.label}`}
                              onClick={() => removeGroup(group.id)}
                              disabled={busy}
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className={styles["comp__empty"]}>No groups selected.</p>
                    )}

                    <Input
                      id="create-account-group-search"
                      label="Search groups"
                      value={search}
                      onChange={(value) => {
                        setSearch(value);
                        setPage(1);
                      }}
                      placeholder="Group name…"
                      disabled={busy}
                      startContent={
                        <SearchSmIcon style={{ width: 18, height: 18 }} aria-hidden />
                      }
                      endContent={
                        search ? (
                          <button
                            type="button"
                            className={styles["comp__clear-search"]}
                            aria-label="Clear search"
                            onClick={() => {
                              setSearch("");
                              setPage(1);
                            }}
                            disabled={busy}
                          >
                            <XCloseIcon style={{ width: 16, height: 16 }} />
                          </button>
                        ) : null
                      }
                    />

                    {searchError ? (
                      <p className={styles["comp__error"]}>{searchError}</p>
                    ) : null}
                    {searchLoading ? (
                      <p className={styles["comp__empty"]}>Loading groups…</p>
                    ) : null}
                    {!searchLoading && !searchError && searchItems.length === 0 ? (
                      <p className={styles["comp__empty"]}>
                        No groups match this search.
                      </p>
                    ) : null}
                  </div>

                  <div className={styles["comp__search-results"]}>
                    {searchItems.map((group) => {
                      const already = selectedIds.includes(group.id);
                      const full =
                        group.seatLimit != null &&
                        group.seatOccupied >= group.seatLimit;
                      return (
                        <div key={group.id} className={styles["comp__result-card"]}>
                          <div>
                            <div>{group.name}</div>
                            <div className={styles["comp__result-meta"]}>
                              {group.kind} · {formatGroupSeatLabel(group)}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="small"
                            variant="secondary"
                            disabled={busy || already || full}
                            onClick={() => addGroup(group)}
                          >
                            {already ? "Selected" : full ? "Full" : "Add"}
                          </Button>
                        </div>
                      );
                    })}
                  </div>

                  <div className={styles["comp__pager"]}>
                    <Button
                      type="button"
                      size="small"
                      variant="secondary"
                      disabled={busy || page <= 1 || searchLoading}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span className={styles["comp__result-meta"]}>
                      Page {searchMeta.page} / {Math.max(1, searchMeta.totalPages)}
                    </span>
                    <Button
                      type="button"
                      size="small"
                      variant="secondary"
                      disabled={
                        busy ||
                        searchLoading ||
                        searchMeta.page >= Math.max(1, searchMeta.totalPages)
                      }
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            ) : null}

            {tab === "security" ? (
              <div className={styles["comp__panel"]} role="tabpanel">
                <div className={styles["comp__intro"]}>
                  <h3 className={styles["comp__section-title"]}>Security</h3>
                  <p className={styles["comp__subtitle"]}>
                    Set a strong password now, or send a setup link via the existing
                    password-set email (7 days). If mail is not configured, create still
                    succeeds and reports that honestly.
                  </p>
                </div>
                <div className={styles["comp__panel-content"]}>
                  <div className={styles["comp__mode-list"]}>
                    <button
                      type="button"
                      className={`${styles["comp__mode-option"]} ${
                        securityMode === "invite"
                          ? styles["comp__mode-option--active"]
                          : ""
                      }`}
                      disabled={busy}
                      onClick={() => {
                        setSecurityMode("invite");
                        setPasswordError("");
                      }}
                    >
                      <span className={styles["comp__mode-label"]}>
                        Send setup link
                      </span>
                      <p className={styles["comp__mode-hint"]}>
                        Uses the hashed single-use setup token email. Requires
                        transactional mail (Resend or Gmail OAuth).
                      </p>
                    </button>
                    <button
                      type="button"
                      className={`${styles["comp__mode-option"]} ${
                        securityMode === "manual"
                          ? styles["comp__mode-option--active"]
                          : ""
                      }`}
                      disabled={busy}
                      onClick={() => setSecurityMode("manual")}
                    >
                      <span className={styles["comp__mode-label"]}>
                        Set password manually
                      </span>
                      <p className={styles["comp__mode-hint"]}>
                        Strong password required (≥8). No invite email is sent.
                      </p>
                    </button>
                  </div>

                  {securityMode === "manual" ? (
                    <Input
                      id="create-account-password"
                      label="Password"
                      type="password"
                      value={password}
                      required
                      disabled={busy}
                      error={passwordError}
                      onChange={(value) => {
                        setPassword(value);
                        setPasswordError("");
                        setSubmitError(null);
                      }}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}

            {submitError ? <p className={styles["comp__error"]}>{submitError}</p> : null}
            {submitNotice ? <p className={styles["comp__notice"]}>{submitNotice}</p> : null}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            {submitNotice ? "Close" : "Cancel"}
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={isSubmitting}
            disabled={isSubmitting || Boolean(submitNotice)}
          >
            Create account
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};
